import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material'
import SearchBox from '@/apps/legacy/components/portal/SearchBox'
import PaginationControl from '@/apps/legacy/components/portal/PaginationControl'
import { CustomButton } from '@/apps/legacy/components/portal/CustomButton'
import { Drug } from '@/features/internal/drugs/types/soapTemplate'

interface DrugTableProps {
  /**
   * テーブルのモード（薬剤一覧 or 薬剤選択）
   */
  mode: 'drugs' | 'selection'

  /**
   * 表示する薬剤データ
   */
  data: Drug[]

  /**
   * 読み込み中フラグ
   */
  loading?: boolean

  /**
   * 検索クエリ文字列
   */
  searchTerm?: string

  /**
   * 検索クエリ変更時のコールバック
   */
  onSearchChange?: (query: string) => void

  /**
   * 検索実行時のコールバック
   */
  onSearch?: () => void

  /**
   * 現在のページ番号
   */
  currentPage?: number

  /**
   * 総ページ数
   */
  totalPages?: number

  /**
   * ページ変更時のコールバック
   */
  onPageChange?: (page: number) => void

  /**
   * 詳細表示ボタンクリック時のコールバック
   */
  onViewDetails?: (id: string) => void

  /**
   * 選択が変更されたときのコールバック（選択モードのみ）
   */
  onSelectionChange?: (selectedIds: string[]) => void

  /**
   * 現在選択されている薬剤IDのリスト（選択モードのみ）
   */
  selectedDrugIds?: string[]
}

/**
 * 医薬品リストをテーブル形式で表示するコンポーネント
 */
const DrugTable: React.FC<DrugTableProps> = ({
  mode,
  data,
  loading = false,
  searchTerm = '',
  onSearchChange,
  onSearch,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onViewDetails,
  onSelectionChange,
  selectedDrugIds = [],
}) => {
  // 検索実行ハンドラー
  const handleSearch = () => {
    if (onSearch) {
      onSearch()
    } else if (onSearchChange) {
      onSearchChange(searchTerm)
    }
  }

  // 選択変更ハンドラー（選択モードのみ）
  const handleSelectionChange = (id: string, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedDrugIds, id])
      } else {
        onSelectionChange(selectedDrugIds.filter((drugId) => drugId !== id))
      }
    }
  }

  // すべて選択/解除ハンドラー
  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange(data.map((drug) => drug.id))
      } else {
        onSelectionChange([])
      }
    }
  }

  // 現在すべて選択されているかチェック
  const isAllSelected =
    data.length > 0 &&
    selectedDrugIds.length === data.length &&
    data.every((drug) => selectedDrugIds.includes(drug.id))

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* 検索ボックス */}
        {onSearchChange && (
          <Box sx={{ flex: 1 }}>
            <SearchBox
              placeholder="医薬品名やYJコードで検索..."
              value={searchTerm}
              onChange={onSearchChange}
              onSearch={handleSearch}
            />
          </Box>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{ mb: 2, maxHeight: 500, overflow: 'auto' }}
      >
        <Table stickyHeader aria-label="医薬品テーブル">
          <TableHead>
            <TableRow>
              {/* 選択モードの場合はチェックボックス列を追加 */}
              {mode === 'selection' && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    indeterminate={selectedDrugIds.length > 0 && !isAllSelected}
                  />
                </TableCell>
              )}

              <TableCell>YJコード</TableCell>
              <TableCell>商品名</TableCell>
              <TableCell>製造元</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={mode === 'selection' ? 5 : 4}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={mode === 'selection' ? 5 : 4}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography>データがありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((drug) => (
                <TableRow key={drug.id} hover>
                  {/* 選択モードの場合はチェックボックス列を追加 */}
                  {mode === 'selection' && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDrugIds.includes(drug.id)}
                        onChange={(e) =>
                          handleSelectionChange(drug.id, e.target.checked)
                        }
                      />
                    </TableCell>
                  )}

                  <TableCell>{drug.yj_code}</TableCell>
                  <TableCell>
                    {drug.product_name ? (
                      drug.product_name
                    ) : (
                      <span style={{ color: '#666' }}>
                        【般】{drug.general_name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{drug.manufacturer}</TableCell>
                  <TableCell align="right">
                    {onViewDetails && (
                      <CustomButton
                        variant="outlined"
                        size="small"
                        onClick={() => onViewDetails(drug.id)}
                      >
                        詳細
                      </CustomButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション（総ページ数が1より大きい場合のみ表示） */}
      {onPageChange && totalPages > 1 && (
        <PaginationControl
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  )
}

export default DrugTable
